import { useState, useEffect, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const getTimezoneAbbreviation = (date: Date): string => {
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === "timeZoneName");
    if (timeZonePart) {
      return timeZonePart.value;
    }
    const timeZoneOffset = date.getTimezoneOffset();
    const hours = Math.floor(Math.abs(timeZoneOffset) / 60);
    const minutes = Math.abs(timeZoneOffset) % 60;
    const sign = timeZoneOffset <= 0 ? "+" : "-";
    return `UTC${sign}${hours.toString().padStart(2, "0")}${minutes > 0 ? ":" + minutes.toString().padStart(2, "0") : ""}`;
  } catch {
    return "Local";
  }
};

const formatDateTimeForDisplay = (dateTimeString: string): string => {
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hour12 = date.getHours() % 12 || 12;
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const timezone = getTimezoneAbbreviation(date);

    return `${month}-${day}-${year} ${hour12}:00 ${ampm} ${timezone}`;
  } catch {
    return dateTimeString;
  }
};

const formatDateTimeForLocalVerbose = (dateTimeString: string): string => {
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hour12 = date.getHours() % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const timezone = getTimezoneAbbreviation(date);

    return `${month} ${day}, ${year}, ${hour12}:${minutes} ${ampm} ${timezone}`;
  } catch {
    return dateTimeString;
  }
};

const formatDateTimeForUTC = (dateTimeString: string): string => {
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;

    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const year = date.getUTCFullYear();
    const hour12 = date.getUTCHours() % 12 || 12;
    const ampm = date.getUTCHours() >= 12 ? "PM" : "AM";

    return `${month}-${day}-${year} ${hour12}:00 ${ampm} UTC`;
  } catch {
    return dateTimeString;
  }
};

const formatDateTimeForZulu = (dateTimeString: string): string => {
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;

    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const year = date.getUTCFullYear();
    const hours24 = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours24}${minutes}Z`;
  } catch {
    return dateTimeString;
  }
};

const formatRunHourSuffix = (
  runTime: string | null,
  runHourMap?: Record<string, string>,
): string => {
  if (!runTime || !runHourMap) return "";
  const runHour = runHourMap[runTime];
  if (!runHour) return "";
  return ` (${runHour}Z)`;
};

const formatRunTimeForDisplay = (runTime: string | null) => {
  if (!runTime) return "N/A";
  return formatDateTimeForDisplay(runTime);
};

interface ModelRunSelectorProps {
  selectedRunTime?: string | null;
  onRunTimeSelect: (runTime: string) => void;
  weatherModelName?: string;
  className?: string;
  availableModelRuns?: string[];
  runHourMap?: Record<string, string>;
  isLoading?: boolean;
}

export function ModelRunSelector({
  selectedRunTime,
  onRunTimeSelect,
  weatherModelName = "weather model",
  className,
  availableModelRuns = [],
  runHourMap,
  isLoading = false,
}: ModelRunSelectorProps) {
  const MODEL_RUN_TIMES = useMemo(() => {
    return availableModelRuns.sort((a, b) => {
      try {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });
  }, [availableModelRuns]);

  const GROUPED_RUN_TIMES = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    MODEL_RUN_TIMES.forEach((runTime) => {
      const formattedDateTime = formatDateTimeForDisplay(runTime);
      const datePart = formattedDateTime.split(" ")[0];
      if (!grouped[datePart]) {
        grouped[datePart] = [];
      }
      grouped[datePart].push(runTime);
    });
    return grouped;
  }, [MODEL_RUN_TIMES]);

  const sortedDates = useMemo(() => {
    return Object.keys(GROUPED_RUN_TIMES).sort((a, b) => {
      const [aMonth, aDay, aYear] = a.split("-").map(Number);
      const [bMonth, bDay, bYear] = b.split("-").map(Number);
      const dateA = new Date(aYear, aMonth - 1, aDay);
      const dateB = new Date(bYear, bMonth - 1, bDay);
      return dateB.getTime() - dateA.getTime();
    });
  }, [GROUPED_RUN_TIMES]);

  const findMatchingRun = (isoString: string | null | undefined): string => {
    if (!isoString || MODEL_RUN_TIMES.length === 0) return "";
    const targetTime = new Date(isoString).getTime();
    const match = MODEL_RUN_TIMES.find(
      (run) => new Date(run).getTime() === targetTime,
    );
    return match || "";
  };

  const [currentRunTime, setCurrentRunTime] = useState(() =>
    findMatchingRun(selectedRunTime),
  );

  useEffect(() => {
    if (selectedRunTime) {
      const matchingRun = findMatchingRun(selectedRunTime);
      if (matchingRun && matchingRun !== currentRunTime) {
        setCurrentRunTime(matchingRun);
      }
    }
  }, [selectedRunTime, MODEL_RUN_TIMES]);

  useEffect(() => {
    if (MODEL_RUN_TIMES.length > 0 && !currentRunTime) {
      setCurrentRunTime(MODEL_RUN_TIMES[0]);
    }
  }, [MODEL_RUN_TIMES, currentRunTime]);

  const handleRunTimeChange = (runTime: string) => {
    setCurrentRunTime(runTime);
    onRunTimeSelect(runTime);
  };

  if (MODEL_RUN_TIMES.length === 0) {
    if (isLoading) {
      return (
        <div className={`relative ${className || ""}`}>
          <div className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs font-medium cursor-not-allowed opacity-60">
            <span className="opacity-70 mr-1">Run:</span>{" "}
            {formatRunTimeForDisplay(currentRunTime || selectedRunTime || null)}
          </div>
        </div>
      );
    }
    return (
      <div className={`relative ${className || ""}`}>
        <div className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs font-medium cursor-not-allowed opacity-60">
          <span className="opacity-70 mr-1">Run:</span> No runs available
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className={`relative ${className || ""}`}>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <DropdownMenuTrigger asChild>
                  <div className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs font-medium hover:bg-secondary/80 cursor-pointer transition-colors">
                    <span className="opacity-70 mr-1">Run:</span>{" "}
                    {formatRunTimeForDisplay(currentRunTime)}
                    {formatRunHourSuffix(currentRunTime, runHourMap)}
                  </div>
                </DropdownMenuTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-xs bg-popover text-popover-foreground border border-border shadow-md"
            >
              {currentRunTime ? (
                <div className="space-y-2">
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                      Local Time
                    </div>
                    <div className="font-medium">
                      {formatDateTimeForLocalVerbose(currentRunTime)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                      UTC Time
                    </div>
                    <div className="font-medium">
                      {formatDateTimeForUTC(currentRunTime)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                      Zulu Time (NWS Format)
                    </div>
                    <div className="font-mono text-sm">
                      {formatDateTimeForZulu(currentRunTime)}
                    </div>
                  </div>
                </div>
              ) : (
                "No run time selected"
              )}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            align="start"
            className="max-h-[400px] overflow-y-auto w-[320px]"
          >
            <DropdownMenuLabel>Model Run Time</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="p-1">
              <div className="text-xs text-muted-foreground px-2 py-2 mb-2">
                Available model run times for {weatherModelName}. More recent
                runs typically provide more accurate forecasts.
              </div>
              {sortedDates.map((date) => (
                <div key={date} className="mb-2">
                  <div className="text-xs font-semibold bg-muted text-secondary-foreground px-2 py-1 rounded-sm sticky top-0">
                    {date}
                  </div>
                  <DropdownMenuRadioGroup
                    value={currentRunTime}
                    onValueChange={handleRunTimeChange}
                  >
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {GROUPED_RUN_TIMES[date].map((runTime) => (
                        <Tooltip key={runTime}>
                          <TooltipTrigger asChild>
                            <span className="block">
                              <DropdownMenuRadioItem
                                value={runTime}
                                className="text-sm py-1 px-2 pl-6"
                              >
                                {formatDateTimeForDisplay(runTime)
                                  .split(" ")
                                  .slice(1)
                                  .join(" ")}
                                {formatRunHourSuffix(runTime, runHourMap)}
                              </DropdownMenuRadioItem>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className="max-w-xs bg-popover text-popover-foreground border border-border shadow-md"
                          >
                            <div className="space-y-2">
                              <div>
                                <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                                  Local Time
                                </div>
                                <div className="font-medium">
                                  {formatDateTimeForLocalVerbose(runTime)}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                                  UTC Time
                                </div>
                                <div className="font-medium">
                                  {formatDateTimeForUTC(runTime)}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-muted-foreground mb-0.5">
                                  Zulu Time (NWS Format)
                                </div>
                                <div className="font-mono text-sm">
                                  {formatDateTimeForZulu(runTime)}
                                </div>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </DropdownMenuRadioGroup>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
}
